import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
  Card,
  Autocomplete,
  Tabs,
  Tab,
  ListItem,
  Checkbox,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  DeleteSweep as BulkDeleteIcon,
  Add as AddIcon,
  CardGiftcard as ReferralIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getRegistrations, getRegistrationById, createRegistration, deleteRegistration, bulkDeleteRegistrations, getUsers, getPartnerAdminRegistrations } from '../../api/adminApi';
import { getAllExams } from '../../api/examApi';
import { useToast } from '../../contexts/ToastContext';
import { useAdmin } from '../../contexts/AdminContext';
import ModernButton from '../../components/common/ModernButton';
import PageHeader from '../../components/common/PageHeader';
import FilterCard from '../../components/common/FilterCard';
import GroupedAccordionList from '../../components/common/GroupedAccordionList';
import ConfirmDialog from '../../components/dialogs/ConfirmDialog';
import ViewDetailsDialog from '../../components/dialogs/ViewDetailsDialog';
import useGroupedData from '../../hooks/useGroupedData';

const RegistrationsManagement = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [currentTab, setCurrentTab] = useState('real');
  const [productTypeFilter, setProductTypeFilter] = useState('');
  const [scoreFilter, setScoreFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewDialog, setViewDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [users, setUsers] = useState([]);
  const [exams, setExams] = useState([]);
  const [createForm, setCreateForm] = useState({
    userId: null,
    productId: null,
    productType: 'Exam',
  });
  const { showToast } = useToast();
  const { isPlatformAdmin } = useAdmin();

  // Use the groupedData hook for accordion management
  const {
    groupsArray,
    isGroupExpanded,
    toggleGroup,
  } = useGroupedData({
    data: registrations,
    getGroupKey: (reg) => reg.productId?._id || 'unknown',
    getGroupMeta: (reg) => ({
      examName: reg.productId?.name || reg.productId?.label || 'Unknown Exam',
      examDate: reg.productId?.startDate,
    }),
    itemsKey: 'registrations',
  });

  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (productTypeFilter) params.productType = productTypeFilter;
      if (scoreFilter !== '') params.hasScore = scoreFilter;

      // Use partner-scoped API for non-platform admins
      const { data } = isPlatformAdmin
        ? await getRegistrations(params)
        : await getPartnerAdminRegistrations(params);

      // Filter based on current tab (Real vs Practice)
      // Handle both response formats: { data: { registrations } } and { registrations }
      const allRegistrations = data.data?.registrations || data.registrations || [];
      const filteredRegistrations = allRegistrations.filter(reg => {
        if (!reg.productId) return false;
        const isPractice = reg.productId.isPractice || false;
        return currentTab === 'practice' ? isPractice : !isPractice;
      });

      setRegistrations(filteredRegistrations);
    } catch (error) {
      console.error('Failed to fetch registrations:', error);
      showToast('Failed to fetch registrations', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, productTypeFilter, scoreFilter, currentTab, isPlatformAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(1);
    setSelectedIds([]);
  };

  const fetchUsersAndExams = async () => {
    try {
      const [usersRes, examsRes] = await Promise.all([
        getUsers({ limit: 1000 }),
        getAllExams(),
      ]);
      setUsers(usersRes.data.users || []);
      setExams(examsRes.data || []);
    } catch (error) {
      console.error('Failed to load users and exams:', error);
      showToast('Failed to load users and exams', 'error');
    }
  };

  const handleOpenCreateDialog = () => {
    fetchUsersAndExams();
    setCreateDialog(true);
  };

  const handleCreateRegistration = async () => {
    if (!createForm.userId || !createForm.productId) {
      showToast('Please select both user and exam', 'warning');
      return;
    }

    try {
      await createRegistration({
        userId: createForm.userId,
        productId: createForm.productId,
        productType: createForm.productType,
      });
      showToast('Registration created successfully', 'success');
      setCreateDialog(false);
      setCreateForm({ userId: null, productId: null, productType: 'Exam' });
      fetchRegistrations();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to create registration', 'error');
    }
  };

  const handleViewRegistration = async (registration) => {
    try {
      const { data } = await getRegistrationById(registration._id);
      setViewDialog(data.registration);
    } catch (error) {
      showToast('Failed to load registration details', 'error');
    }
  };

  const handleDeleteRegistration = async () => {
    if (!deleteDialog) return;
    try {
      await deleteRegistration(deleteDialog._id);
      showToast('Registration deleted successfully', 'success');
      setDeleteDialog(null);
      fetchRegistrations();
    } catch (error) {
      showToast('Failed to delete registration', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      showToast('Please select registrations to delete', 'warning');
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.length} registrations?`)) return;

    try {
      await bulkDeleteRegistrations(selectedIds);
      showToast(`${selectedIds.length} registrations deleted successfully`, 'success');
      setSelectedIds([]);
      fetchRegistrations();
    } catch (error) {
      showToast('Failed to delete registrations', 'error');
    }
  };

  const handleToggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Render individual registration item in accordion
  const renderRegistrationItem = (registration) => (
    <ListItem
      key={registration._id}
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        py: 2.5,
        px: 3,
        backgroundColor: registration.userId?.referralId ? 'rgba(46, 125, 50, 0.04)' : 'background.paper',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {isPlatformAdmin && (
          <Checkbox
            size="small"
            checked={selectedIds.includes(registration._id)}
            onChange={() => handleToggleSelection(registration._id)}
            sx={{ mr: 1 }}
          />
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {registration.userId?.firstName} {registration.userId?.lastName}
            </Typography>
            {registration.userId?.referralId && (
              <Tooltip title="Registered via Referral" arrow>
                <Chip
                  icon={<ReferralIcon sx={{ fontSize: 14 }} />}
                  label="Referral"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                />
              </Tooltip>
            )}
            <Chip
              label={registration.score ? 'Has Score' : 'No Score'}
              color={registration.score ? 'success' : 'default'}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.75rem' }}
            />
            <Chip
              label={registration.hasStarted ? 'Started' : 'Pending'}
              color={registration.hasStarted ? 'info' : 'default'}
              size="small"
              sx={{ height: 20, fontSize: '0.75rem' }}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {registration.userId?.email}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Created: {format(new Date(registration.createdAt), 'dd-MM-yyyy')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewRegistration(registration)}
              sx={{ color: 'primary.main', '&:hover': { backgroundColor: 'primary.50' } }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isPlatformAdmin && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => setDeleteDialog(registration)}
                sx={{ color: 'error.main', '&:hover': { backgroundColor: 'error.50' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </ListItem>
  );

  // View dialog fields configuration
  const viewDialogFields = [
    {
      label: 'User',
      render: (data) => data.userId
        ? `${data.userId.firstName} ${data.userId.lastName} (${data.userId.email})`
        : 'N/A',
    },
    {
      label: 'Product',
      render: (data) => data.productId
        ? `${data.productId.name || data.productId.label || 'N/A'} (${data.productType})`
        : data.productType,
    },
    {
      label: 'Has Started',
      render: (data) => data.hasStarted ? 'Yes' : 'No',
      chip: true,
    },
    {
      label: 'Has Score',
      render: (data) => data.score ? 'Yes' : 'No',
      chip: true,
    },
  ];

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Registrations Management"
        subtitle="Manage all exam and product registrations"
        icon={<AssignmentIcon />}
        iconColor="info.main"
        actions={
          isPlatformAdmin && (
            <>
              <ModernButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateDialog}
              >
                Create Registration
              </ModernButton>
              {selectedIds.length > 0 && (
                <ModernButton
                  variant="contained"
                  color="error"
                  startIcon={<BulkDeleteIcon />}
                  onClick={handleBulkDelete}
                >
                  Delete Selected ({selectedIds.length})
                </ModernButton>
              )}
            </>
          )
        }
      />

      {/* Tabs */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            label="Real Exam Registrations"
            value="real"
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.938rem' }}
          />
          <Tab
            label="Practice Exam Registrations"
            value="practice"
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.938rem' }}
          />
        </Tabs>
      </Card>

      {/* Filters */}
      <FilterCard onApply={fetchRegistrations} filterColumns={2}>
        <TextField
          select
          fullWidth
          label="Product Type"
          value={productTypeFilter}
          onChange={(e) => setProductTypeFilter(e.target.value)}
        >
          <MenuItem value="">All Types</MenuItem>
          <MenuItem value="Exam">Exam</MenuItem>
          <MenuItem value="Speaking">Speaking</MenuItem>
          <MenuItem value="Writing">Writing</MenuItem>
        </TextField>
        <TextField
          select
          fullWidth
          label="Score Status"
          value={scoreFilter}
          onChange={(e) => setScoreFilter(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Has Score</MenuItem>
          <MenuItem value="false">No Score</MenuItem>
        </TextField>
      </FilterCard>

      {/* Grouped by Exam - Accordion View */}
      <GroupedAccordionList
        groups={groupsArray}
        itemsKey="registrations"
        isGroupExpanded={isGroupExpanded}
        onToggleGroup={toggleGroup}
        renderItem={renderRegistrationItem}
        groupNameKey="examName"
        groupDateKey="examDate"
        itemLabel="student"
        loading={loading}
      />

      {/* View Dialog */}
      <ViewDetailsDialog
        open={!!viewDialog}
        onClose={() => setViewDialog(null)}
        title="Registration Details"
        data={viewDialog}
        fields={viewDialogFields}
        maxWidth="md"
      />

      {/* Delete Dialog */}
      <ConfirmDialog
        open={!!deleteDialog}
        onClose={() => setDeleteDialog(null)}
        onConfirm={handleDeleteRegistration}
        title="Delete Registration?"
        message="Are you sure you want to delete this registration? This will also delete the associated score if it exists."
        confirmText="Delete"
      />

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Registration</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                value={users.find(u => u._id === createForm.userId) || null}
                onChange={(e, newValue) => {
                  setCreateForm({ ...createForm, userId: newValue?._id || null });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select User" required />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Product Type"
                value={createForm.productType}
                onChange={(e) => setCreateForm({ ...createForm, productType: e.target.value })}
              >
                <MenuItem value="Exam">Exam</MenuItem>
                <MenuItem value="Speaking">Speaking</MenuItem>
                <MenuItem value="Writing">Writing</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={exams}
                getOptionLabel={(option) => option.name || 'Unnamed Exam'}
                value={exams.find(e => e._id === createForm.productId) || null}
                onChange={(e, newValue) => {
                  setCreateForm({ ...createForm, productId: newValue?._id || null });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Exam" required />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ModernButton onClick={() => setCreateDialog(false)}>Cancel</ModernButton>
          <ModernButton variant="contained" onClick={handleCreateRegistration}>
            Create
          </ModernButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegistrationsManagement;
